import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseApiError } from '@saas-kit/lib';
import {
  Button,
  Drawer,
  ErrorBanner,
  Field,
  InputField,
  Select,
  Textarea,
} from '@saas-kit/ui';

import type {
  AdminPlan,
  BillingInterval,
  CreatePlanPayload,
  UpdatePlanPayload,
} from './api';
import {
  PLAN_INTERVALS,
  emptyPlanForm,
  planFormFromPlan,
  planFormToCreate,
  planFormToUpdate,
  type PlanFormState,
} from './planForm';

type Mode = 'create' | 'edit';

interface Props {
  open: boolean;
  mode: Mode;
  plan?: AdminPlan | null;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: CreatePlanPayload | UpdatePlanPayload) => Promise<void>;
}

export function PlanFormDialog({
  open,
  mode,
  plan,
  isPending,
  onOpenChange,
  onSubmit,
}: Props): JSX.Element {
  const { t } = useTranslation('adminPlans');
  const [form, setForm] = useState<PlanFormState>(emptyPlanForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(plan ? planFormFromPlan(plan) : emptyPlanForm());
  }, [open, plan]);

  function update<K extends keyof PlanFormState>(
    key: K,
    value: PlanFormState[K],
  ): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(event: React.FormEvent): void {
    event.preventDefault();
    setError(null);
    const trialDays = Number.parseInt(form.trial_days, 10);
    if (Number.isNaN(trialDays) || trialDays < 0) {
      setError(t('form.errors.trial_days'));
      return;
    }
    const payload =
      mode === 'create'
        ? planFormToCreate(form, trialDays)
        : planFormToUpdate(form, trialDays);
    void (async () => {
      try {
        await onSubmit(payload);
        onOpenChange(false);
      } catch (err) {
        setError(parseApiError(err).message);
      }
    })();
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={t(mode === 'create' ? 'form.create_title' : 'form.edit_title')}
      description={t(
        mode === 'create' ? 'form.create_description' : 'form.edit_description',
      )}
      testId="plan-form-drawer"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('form.cancel')}
          </Button>
          <Button type="submit" form="plan-form" disabled={isPending}>
            {isPending ? t('form.saving') : t('form.submit')}
          </Button>
        </div>
      }
    >
      <form id="plan-form" className="space-y-4" onSubmit={handleSubmit}>
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}
        <InputField
          label={t('form.code')}
          name="code"
          required
          disabled={mode === 'edit'}
          hint={t('form.code_hint')}
          value={form.code}
          onChange={(e) => update('code', e.target.value)}
        />
        <InputField
          label={t('form.name')}
          name="name"
          required
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
        />
        <Field label={t('form.description')} htmlFor="plan-description">
          <Textarea
            id="plan-description"
            rows={3}
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('form.billing_interval')} htmlFor="plan-interval">
            <Select
              id="plan-interval"
              value={form.billing_interval}
              onChange={(e) =>
                update('billing_interval', e.target.value as BillingInterval)
              }
            >
              {PLAN_INTERVALS.map((interval) => (
                <option key={interval} value={interval}>
                  {t(`intervals.${interval}`)}
                </option>
              ))}
            </Select>
          </Field>
          <InputField
            label={t('form.currency')}
            name="currency"
            required
            maxLength={3}
            value={form.currency}
            onChange={(e) => update('currency', e.target.value.toUpperCase())}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <InputField
            label={t('form.unit_amount')}
            name="unit_amount"
            required
            inputMode="decimal"
            placeholder="0.00"
            value={form.unit_amount}
            onChange={(e) => update('unit_amount', e.target.value)}
          />
          <InputField
            label={t('form.trial_days')}
            name="trial_days"
            type="number"
            min={0}
            max={365}
            value={form.trial_days}
            onChange={(e) => update('trial_days', e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => update('is_active', e.target.checked)}
          />
          {t('form.is_active')}
        </label>
      </form>
    </Drawer>
  );
}
